import {
  addDays,
  compareAsc,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfDay,
  format,
  Interval,
  isBefore,
  isSameDay,
  isWeekend,
  isWithinInterval,
  startOfDay,
  startOfToday,
  startOfTomorrow,
  subDays,
  subMinutes,
} from 'date-fns';

import { capacitySelectors } from '@/common/selectors/capacities';
import { OrderEntity, ProjectEntity, TradeEntity } from '@/common/types/entities';
import { usePauseStore } from '@/features/pauses/pauseStore';
import { ScheduleViewStore } from '@/features/scheduleViews/scheduleViewStore';
import { ProjectContributorGroupFragment, TradeFragment } from '@/graphql/__generated__/graphql';
import { getRandomId } from '@/helpers/utils/strings';
import { LoggingService } from '@/services/logging/types';
import { getScheduler } from '@/services/store/integrations/scheduler';
import {
  getResourcesWithEvents,
  isOrderFilteredOut,
} from '@/services/store/schedule/actions/filter';

import { useScheduleViewStore } from '../scheduleViews/scheduleViewStore';
import { ScheduleViewSettingChangedCapacityCurveVisibilityEvent } from './analytics';
import {
  AggregationResult,
  CapacitiesData,
  DailyTradeSummary,
  TradesProjectCount,
  TradesSummaryByDate,
  WorkerThresholdExceededAlert,
} from './types';

// constants
export const CAPACITIES_CURVE = {
  CELL: {
    HOVER_CLOSE_DELAY: 250,
    ROOT_OFFSET: 16,
    TOP_MARGIN_PX: 80 + 16,
  },
  COLUMN: {
    BOTTOM_MARGIN: 8,
    BORDER_WIDTH: 1,
  },
  LABEL_HEIGHT: 24,
  LABEL_GAP: 4, // Gap between label and curves (see summaryRow.css margin-bottom)
  PILLS: {
    GAP: 2,
    PADDING: 3,
    CORNER_RADIUS: 4,
    MAX_HEIGHT: 10,
    MIN_HEIGHT: 3,
  },
  GRADIENT: {
    LINE_WIDTH: 2,
    OPACITY: {
      START: 0.8,
      END: 0.13,
    },
  },
  STACKED_BAR: {
    PADDING: 3,
    CORNER_RADIUS: 4,
  },
  RESIZER: {
    MIN_HEIGHT: 100,
    MAX_HEIGHT: 500,
    DEFAULT_HEIGHT: 300,
    POSITION_UPDATE_DELAY: 50,
  },
};

export const CAPACITY_ROW_HEADER_ID = 'capacity-row-header';

const getTargetTick = (date: Date) => {
  const targetDate = formatDateKey(date);

  const allTicks = document.querySelectorAll('.b-timeaxis-tick') as NodeListOf<HTMLElement>;

  for (const tick of allTicks) {
    const startDate = tick.getAttribute('data-start-date');
    const endDate = tick.getAttribute('data-end-date');

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const target = new Date(targetDate);

      if (target >= start && target < end) {
        return tick;
      }
    }
  }

  return null;
};

export const highlightTickForDate = (date: Date) => {
  const targetTick = getTargetTick(date);

  if (targetTick) {
    const overlay = document.createElement('div');
    overlay.className = 'pulse-overlay';

    const originalPosition = targetTick.style.position;
    targetTick.style.position = 'relative';

    targetTick.appendChild(overlay);

    overlay.addEventListener('animationend', () => {
      overlay.remove();
      targetTick.style.position = originalPosition;
    });
  }
};

// data processing
export const formatDateKey = (date: Date) => format(date, 'yyyy-MM-dd');

export const getVisibleOrdersMap = (orders: Map<string, OrderEntity>): Map<string, OrderEntity> => {
  const scheduler = getScheduler();
  const scheduleViewStore = useScheduleViewStore();
  if (!scheduler || !scheduleViewStore.isFilterActive) return orders;

  const resourcesWithEvents = getResourcesWithEvents(scheduler);

  const visibleOrders = new Map<string, OrderEntity>();

  for (const [orderId, order] of orders.entries()) {
    if (!isOrderFilteredOut(order, scheduleViewStore.currentFilter, resourcesWithEvents)) {
      visibleOrders.set(orderId, order);
    }
  }

  return visibleOrders;
};

export const getCapacitiesData = (
  orders: Map<string, OrderEntity>,
  trades: Map<string, TradeFragment>,
  contributorGroups: Map<string, ProjectContributorGroupFragment>,
  isStartDateDuringWorkingTime: (date: Date, calendarId: string) => boolean,
): CapacitiesData => {
  const maxTradeAppearances: TradesProjectCount = {};
  const maxTradeWorkerCounts: TradesProjectCount = {};
  const maxContributorGroupWorkerCounts: Record<
    string,
    Map<string, { workers: number; orderIds: string[] }>
  > = {};
  const orderDates: Date[] = [];
  const tradesSummaryByDate: TradesSummaryByDate = {};
  const tradesUsedForSummaries: Map<string, TradeEntity> = new Map<string, TradeEntity>();

  for (const order of orders.values()) {
    const trade = trades.get(order.tenantTradeVariation.id);
    if (!trade) continue;

    tradesUsedForSummaries.set(trade.id, trade);
    orderDates.push(order.startAt, order.finishAt);
    const tradeId = trade.id;
    const color = trade.color;
    const name = trade.name;
    const start = startOfDay(new SchedulingDate(order.startAt));
    const end = endOfDay(subDays(new SchedulingDate(order.finishAt), 1));

    let currentDate = start;
    while (currentDate.getTime() <= end.getTime()) {
      const dayKey = formatDateKey(currentDate);
      if (!tradesSummaryByDate[dayKey]) tradesSummaryByDate[dayKey] = {};
      if (!tradesSummaryByDate[dayKey][tradeId]) {
        tradesSummaryByDate[dayKey][tradeId] = {
          tradeId,
          color,
          name,
          count: 0,
          workerCount: 0,
          contributorGroups: new Map(),
          orderIds: new Set<string>(),
        };
      }

      if (!maxTradeAppearances[tradeId]) maxTradeAppearances[tradeId] = 0;
      if (!maxTradeWorkerCounts[tradeId]) maxTradeWorkerCounts[tradeId] = 0;

      if (isStartDateDuringWorkingTime(currentDate, order.calendar.id)) {
        tradesSummaryByDate[dayKey][tradeId].count++;
        tradesSummaryByDate[dayKey][tradeId].workerCount += order.estimatedWorkers || 0;
        tradesSummaryByDate[dayKey][tradeId].orderIds.add(order.id);
        const contributorGroup =
          order.contributorGroup && contributorGroups.get(order.contributorGroup.id);
        if (contributorGroup) {
          tradesSummaryByDate[dayKey][tradeId].contributorGroups.set(
            contributorGroup.id,
            contributorGroup,
          );

          if (!maxContributorGroupWorkerCounts[dayKey]) {
            maxContributorGroupWorkerCounts[dayKey] = new Map<
              string,
              { workers: number; orderIds: string[] }
            >();
          }
          if (!maxContributorGroupWorkerCounts[dayKey].has(contributorGroup.id)) {
            maxContributorGroupWorkerCounts[dayKey].set(contributorGroup.id, {
              workers: 0,
              orderIds: [],
            });
          }
          const maxContributorGroupWorkerCount = maxContributorGroupWorkerCounts[dayKey].get(
            contributorGroup.id,
          );
          if (maxContributorGroupWorkerCount) {
            maxContributorGroupWorkerCount.workers += order.estimatedWorkers || 0;
            maxContributorGroupWorkerCount.orderIds.push(order.id);
          }
        }
      }

      // Update global max trade appearence count for sorting
      if (tradesSummaryByDate[dayKey][tradeId].count > maxTradeAppearances[tradeId]) {
        maxTradeAppearances[tradeId] = tradesSummaryByDate[dayKey][tradeId].count;
      }

      // Update global max trade worker count for sorting
      if (tradesSummaryByDate[dayKey][tradeId].workerCount > maxTradeWorkerCounts[tradeId]) {
        maxTradeWorkerCounts[tradeId] = tradesSummaryByDate[dayKey][tradeId].workerCount;
      }

      currentDate = addDays(currentDate, 1);
    }
  }

  const dailyTradeSummaries = getDailyTradeSummaries(tradesSummaryByDate);

  const orderedDates = orderDates.sort(compareAsc);

  const timeRange = {
    start: orderedDates[0] ?? startOfToday(),
    // subMinutes here because the interval is exclusive, and isWithinInterval also checks the boundaries
    end: subMinutes(orderedDates[orderedDates.length - 1] ?? startOfTomorrow(), 1),
  };

  return {
    dailyTradeSummaries,
    maxContributorGroupWorkerCounts,
    maxTradeAppearances,
    maxTradeWorkerCounts,
    timeRange,
    tradesUsedForSummaries,
  };
};

export const getDailyTradeSummaries = (
  tradesSummaryByDate: TradesSummaryByDate,
): Record<string, DailyTradeSummary[]> => {
  // Convert nested records to Record<string, DailyTradeSummary[]>
  const dailyTradeSummaries: Record<string, DailyTradeSummary[]> = {};
  for (const dayKey in tradesSummaryByDate) {
    dailyTradeSummaries[dayKey] = Object.values(tradesSummaryByDate[dayKey]);
  }

  return dailyTradeSummaries;
};

export const sortTradeSummariesByMaxValues = (
  tradeSummaries: DailyTradeSummary[],
  maxValuesToSortBy: Record<string, number>,
): DailyTradeSummary[] => {
  return [...tradeSummaries].sort((a, b) => {
    const countDiff = maxValuesToSortBy[a.tradeId] - maxValuesToSortBy[b.tradeId];
    return countDiff !== 0 ? countDiff : a.name.localeCompare(b.name);
  });
};

export const getSortedTradeSummariesForDate = (
  date: Date,
  tradeSummariesByDate: Record<string, DailyTradeSummary[]>,
  maxValuesToSortBy: Record<string, number>,
) => {
  const dateKey = formatDateKey(date);
  const tradeSummaries = tradeSummariesByDate[dateKey] || [];

  return sortTradeSummariesByMaxValues(tradeSummaries, maxValuesToSortBy);
};

export const getSortedAggregatedTradeSummariesForTimespan = (
  startDate: Date,
  endDate: Date,
  tradeSummariesByDate: Record<string, DailyTradeSummary[]>,
  maxValuesToSortBy: Record<string, number>,
) => {
  const summaries: Record<string, DailyTradeSummary> = {};

  const daysInRange = eachDayOfInterval({
    start: startOfDay(startDate),
    end: endOfDay(subDays(endDate, 1)),
  });
  if (daysInRange.length === 0) return Object.values(summaries);

  daysInRange.forEach((day) => {
    const dateKey = formatDateKey(day);
    if (tradeSummariesByDate[dateKey]) {
      tradeSummariesByDate[dateKey].forEach((summary) => {
        if (!summaries[summary.tradeId]) {
          summaries[summary.tradeId] = { ...summary };
        } else {
          summaries[summary.tradeId].count = Math.max(
            summaries[summary.tradeId].count,
            summary.count,
          );
          summaries[summary.tradeId].workerCount = Math.max(
            summaries[summary.tradeId].workerCount,
            summary.workerCount,
          );
          summary.contributorGroups.forEach((contributorGroup) => {
            summaries[summary.tradeId].contributorGroups.set(contributorGroup.id, contributorGroup);
          });
        }
      });
    }
  });

  return sortTradeSummariesByMaxValues(Object.values(summaries), maxValuesToSortBy);
};

export const getBackgroundStyle = (startDate: Date, endDate: Date) => {
  const pauseStore = usePauseStore();

  const dayCount = differenceInCalendarDays(endDate, startDate);
  const colors: string[] = [];
  const today = new Date();

  for (let i = 0; i < dayCount; i++) {
    const day = new Date(startDate);
    day.setDate(day.getDate() + i);

    let backgroundColor = 'var(--color-white)';

    const pausesOnThatDay = pauseStore.datesWithPauses.get(
      new SchedulingDate(day).toISOString().slice(0, 10),
    );
    const isPause =
      pausesOnThatDay?.some((p) => !isBefore(day, p.start) && isBefore(day, p.end)) || false;
    const isBreak = isWeekend(day) || isPause;
    const isToday = isSameDay(day, today);

    if (isBreak && isToday) {
      backgroundColor =
        'color-mix(in srgb, var(--color-secondary-bg-light), var(--color-yellow-50))';
    } else if (isBreak) {
      backgroundColor = 'var(--color-secondary-bg-light)';
    } else if (isToday) {
      backgroundColor = 'var(--color-yellow-50)';
    }

    const startPercent = (i / dayCount) * 100;
    const endPercent = ((i + 1) / dayCount) * 100;
    colors.push(`${backgroundColor} ${startPercent}%, ${backgroundColor} ${endPercent}%`);
  }

  return `linear-gradient(to right, ${colors.join(', ')})`;
};

// rendering
export function highlightDailyElementsByTrade(
  tradeId: string | null,
  aggregationMode: 'activities' | 'people',
) {
  if (!tradeId) return;

  const selector = getDailyCurveSelector(aggregationMode);
  const allElements = document.querySelectorAll(selector);

  allElements.forEach((element) => {
    const elementTradeId = element.getAttribute('data-trade-id');
    const isHoveredTrade = elementTradeId === tradeId;
    const testId = element.getAttribute('data-testid');
    const isHighLuminance = element.getAttribute('data-high-luminance') === 'true';

    // For border elements (pill or bar borders), set opacity to 0 when not hovered
    if (
      testId === capacitySelectors.row.curve.pillBorder ||
      testId === capacitySelectors.row.curve.barBorder
    ) {
      element.setAttribute('opacity', isHoveredTrade ? '1' : '0.3');
    } else {
      // For main elements (pills or bars), use the normal opacity behavior
      element.setAttribute('opacity', isHoveredTrade || isHighLuminance ? '1' : '0.3');
    }
  });
}

export function removeHighlighting(aggregationMode: 'activities' | 'people') {
  const selector = getDailyCurveSelector(aggregationMode);
  const allElements = document.querySelectorAll(selector);
  allElements.forEach((element) => {
    element.setAttribute('opacity', '1');
  });
}

export function getSummaryCountLabel(total?: number): HTMLDivElement {
  const label = document.createElement('div');
  label.className = 'summary-feature-count-label';
  label.textContent = total !== undefined ? String(total) : '';
  label.style.height = `${CAPACITIES_CURVE.LABEL_HEIGHT}px`;
  return label;
}

export function getSummaryContainer(
  elementHeight: number,
  backgroundStyle: string,
): HTMLDivElement {
  const container = document.createElement('div');
  container.className = 'summary-feature-wrapper';
  container.style.position = 'relative';
  container.style.width = '100%';
  container.style.height = `${elementHeight}px`;
  container.style.background = backgroundStyle;
  return container;
}

export function getContentfulSummaryContainer(
  elementHeight: number,
  backgroundStyle: string,
): HTMLDivElement {
  const container = getSummaryContainer(elementHeight, backgroundStyle);
  container.classList.add('summary-feature-wrapper--contentful');
  return container;
}

export function getEmptyContainer(
  elementHeight: number,
  backgroundStyle: string,
  startDate: Date,
  summaryTimeRange: Interval,
): string {
  const totalLabel = isWithinInterval(startDate, summaryTimeRange) ? 0 : undefined;
  const container = getSummaryContainer(elementHeight, backgroundStyle);
  const label = getSummaryCountLabel(totalLabel);
  container.appendChild(label);
  return container.outerHTML;
}

// DOM utilities
function getDailyCurveSelector(aggregationMode: 'activities' | 'people'): string {
  return aggregationMode === 'activities'
    ? `[data-testid="${capacitySelectors.row.curve.pill}"], [data-testid="${capacitySelectors.row.curve.pillBorder}"]`
    : `[data-testid="${capacitySelectors.row.curve.bar}"], [data-testid="${capacitySelectors.row.curve.barBorder}"]`;
}

export function isTradeElement(el: unknown): el is Element {
  return el instanceof Element && el.hasAttribute('data-trade-id');
}

// event utilities
export function createMouseEventWithContainerTarget(
  originalEvent: Event,
  containerElement: HTMLElement,
  basePayload: { isDaily: boolean; startDate: Date; endDate: Date },
): {
  type: 'mouseenter';
  mouseEvent: MouseEvent;
  isDaily: boolean;
  startDate: Date;
  endDate: Date;
} {
  const mouseEvent = originalEvent as MouseEvent;
  const containerRect = containerElement.getBoundingClientRect();

  // Send 'mouseenter' to tooltip (not 'mousemove') to keep it visible while moving around the cell
  // Position tooltip at top of column for consistent placement regardless of mouse position
  const adjustedMouseEvent = new MouseEvent(originalEvent.type, {
    bubbles: originalEvent.bubbles,
    cancelable: originalEvent.cancelable,
    composed: originalEvent.composed,
    clientX: containerRect.left + containerRect.width / 2,
    clientY: containerRect.top,
    screenX: mouseEvent.screenX,
    screenY: mouseEvent.screenY,
    button: mouseEvent.button,
    buttons: mouseEvent.buttons,
    relatedTarget: mouseEvent.relatedTarget,
    movementX: mouseEvent.movementX,
    movementY: mouseEvent.movementY,
  });

  // Set the target to the container element
  Object.defineProperty(adjustedMouseEvent, 'target', {
    value: containerElement,
    writable: false,
  });

  return {
    type: 'mouseenter',
    mouseEvent: adjustedMouseEvent,
    ...basePayload,
  };
}

export function toggleCapacities(
  scheduleViewStore: ScheduleViewStore,
  loggingService: LoggingService,
  method: 'shortcut' | 'sidebar',
): void {
  const show = !scheduleViewStore.showCapacities;
  scheduleViewStore.setShowCapacities(show);

  loggingService.trackEvent(
    new ScheduleViewSettingChangedCapacityCurveVisibilityEvent({
      show,
      method,
    }),
  );
}

export function getContributorGroupWorkerThresholdAlerts(
  contributorGroups: Map<string, ProjectContributorGroupFragment>,
  maxContributorGroupWorkerCounts: Record<
    string,
    Map<string, { workers: number; orderIds: string[] }>
  >,
): WorkerThresholdExceededAlert[] {
  const alerts: WorkerThresholdExceededAlert[] = [];

  for (const [dayKey, maxSimultaneousWorkers] of Object.entries(maxContributorGroupWorkerCounts)) {
    for (const [
      contributorGroupId,
      contributorGroupWorkerCount,
    ] of maxSimultaneousWorkers.entries()) {
      const contributorGroup = contributorGroups.get(contributorGroupId);
      if (
        typeof contributorGroup?.workerThreshold === 'number' &&
        contributorGroupWorkerCount.workers > contributorGroup.workerThreshold
      ) {
        alerts.push({
          id: getRandomId(),
          actualWorkerCount: contributorGroupWorkerCount.workers,
          date: new SchedulingDate(dayKey),
          orderIds: contributorGroupWorkerCount.orderIds,
          contributorGroupId: contributorGroupId,
          threshold: contributorGroup.workerThreshold,
          title: contributorGroup.name,
          type: 'contributorGroup',
        });
      }
    }
  }

  return alerts;
}

export function getProjectWorkerThresholdAlerts(
  project: ProjectEntity,
  dailyPeaks: Record<string, { workers: number; activities: number; orderIds: string[] }>,
): WorkerThresholdExceededAlert[] {
  const alerts: WorkerThresholdExceededAlert[] = [];
  if (typeof project.workerThreshold !== 'number') return alerts;

  for (const [dayKey, peaks] of Object.entries(dailyPeaks)) {
    if (peaks.workers > project.workerThreshold) {
      alerts.push({
        id: getRandomId(),
        actualWorkerCount: peaks.workers,
        date: new SchedulingDate(dayKey),
        orderIds: peaks.orderIds,
        threshold: project.workerThreshold,
        title: project.name,
        type: 'project',
      });
    }
  }

  return alerts;
}

export function getAggregatedTradeSummariesForDate(
  dailyTradeSummaries: Record<string, DailyTradeSummary[]>,
  date: Date,
  dailyAggregationCache: Map<
    string,
    { sumOfActivities: AggregationResult; sumOfWorkers: AggregationResult }
  >,
): {
  sumOfWorkers: AggregationResult;
  sumOfActivities: AggregationResult;
} {
  const dateKey = formatDateKey(date);

  const cachedAggregation = dailyAggregationCache.get(dateKey);
  if (cachedAggregation) return cachedAggregation;

  const sumOfActivities: AggregationResult = { key: dateKey, count: 0 };
  const sumOfWorkers: AggregationResult = { key: dateKey, count: 0 };

  const trades = dailyTradeSummaries[dateKey] || [];

  trades.forEach((trade) => {
    sumOfActivities.count += trade.count;
    sumOfWorkers.count += trade.workerCount;
  });

  dailyAggregationCache.set(dateKey, { sumOfActivities, sumOfWorkers });

  return { sumOfActivities, sumOfWorkers };
}

export function getAggregatedTradeSummariesForTimespan(
  dailyTradeSummaries: Record<string, DailyTradeSummary[]>,
  startDate: Date,
  endDate: Date,
  timespanAggregationCache: Map<
    string,
    { maxSumOfWorkers: AggregationResult; maxSumOfActivities: AggregationResult }
  >,
): {
  maxSumOfWorkers: AggregationResult;
  maxSumOfActivities: AggregationResult;
} {
  const intervalStart = startOfDay(startDate);
  const intervalEnd = endOfDay(subDays(endDate, 1));

  const timespanAggregationCacheKey = `${formatDateKey(intervalStart)}-${formatDateKey(intervalEnd)}`;
  const cachedAggregationResult = timespanAggregationCache.get(timespanAggregationCacheKey);
  if (cachedAggregationResult) return cachedAggregationResult;

  const daysInRange = eachDayOfInterval({
    start: intervalStart,
    end: intervalEnd,
  });

  const maxSumOfActivities: AggregationResult = { key: null, count: 0 };
  const maxSumOfWorkers: AggregationResult = { key: null, count: 0 };

  daysInRange.forEach((day) => {
    const dateKey = formatDateKey(day);
    const tradeSummaries = dailyTradeSummaries[dateKey] || [];
    let totalActivityCount = 0;
    let totalWorkerCount = 0;

    tradeSummaries.forEach((tradeSummary) => {
      totalActivityCount += tradeSummary.count;
      totalWorkerCount += tradeSummary.workerCount;
    });

    if (totalActivityCount > maxSumOfActivities.count) {
      maxSumOfActivities.key = dateKey;
      maxSumOfActivities.count = totalActivityCount;
    }
    if (totalWorkerCount > maxSumOfWorkers.count) {
      maxSumOfWorkers.key = dateKey;
      maxSumOfWorkers.count = totalWorkerCount;
    }
  });

  const result = {
    maxSumOfWorkers,
    maxSumOfActivities,
  };

  timespanAggregationCache.set(timespanAggregationCacheKey, result);

  return result;
}

export function getDailyPeaks(dailyTradeSummaries: Record<string, DailyTradeSummary[]>): Record<
  string,
  {
    workers: number;
    activities: number;
    orderIds: string[];
  }
> {
  const dailyPeaks: Record<string, { workers: number; activities: number; orderIds: string[] }> =
    {};

  for (const [dayKey, tradeSummaries] of Object.entries(dailyTradeSummaries)) {
    let dayTotalWorkers = 0;
    let dayTotalActivities = 0;
    const dayOrderIds: Set<string> = new Set();

    tradeSummaries.forEach((tradeSummary) => {
      dayTotalWorkers += tradeSummary.workerCount;
      dayTotalActivities += tradeSummary.count;
      tradeSummary.orderIds.forEach((id) => dayOrderIds.add(id));
    });

    dailyPeaks[dayKey] = {
      workers: dayTotalWorkers,
      activities: dayTotalActivities,
      orderIds: Array.from(dayOrderIds),
    };
  }

  return dailyPeaks;
}

export function getProjectPeaks(
  dailyPeaks: Record<string, { workers: number; activities: number; orderIds: string[] }>,
): {
  workers: number;
  activities: number;
} {
  const projectPeaks = { workers: 0, activities: 0 };
  for (const peak of Object.values(dailyPeaks)) {
    if (peak.workers > projectPeaks.workers) {
      projectPeaks.workers = peak.workers;
    }
    if (peak.activities > projectPeaks.activities) {
      projectPeaks.activities = peak.activities;
    }
  }
  return projectPeaks;
}

export function getContributorGroupWorkerPeaks(
  maxContributorGroupWorkerCounts: Record<
    string,
    Map<string, { workers: number; orderIds: string[] }>
  >,
  contributorGroupIds: string[],
): Record<string, number> {
  const peaks: Record<string, number> = {};

  contributorGroupIds.forEach((groupId) => {
    peaks[groupId] = 0;
  });

  for (const summaries of Object.values(maxContributorGroupWorkerCounts)) {
    for (const [contributorGroupId, count] of summaries) {
      peaks[contributorGroupId] = Math.max(peaks[contributorGroupId], count.workers);
    }
  }

  return peaks;
}
